import { useEffect, useState } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { endpoints } from "../api.js";
import { Icon } from "../components/Icon.jsx";
import { Credits, EmptyState, ErrorBanner, Loading, StatusPill } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocalePath } from "../i18n/hooks.js";
import { useSession } from "../session.jsx";

const RECENT = 6;

function NextStep({ tenant }) {
  const lp = useLocalePath();
  const step = tenant?.nextVerificationStep;
  if (!step) return null;
  const action = {
    accept_terms: { to: lp("/onboarding"), id: "dashboard.next.accept_terms.cta" },
    subscribe: { to: lp("/onboarding"), id: "dashboard.next.subscribe.cta" },
  }[step];
  return (
    <div className={`next-step next-${step}`}>
      <div className="next-step-text">
        <strong>
          <FormattedMessage id={`dashboard.next.${step}.title`} />
        </strong>
        <span className="muted small">
          <FormattedMessage id={`dashboard.next.${step}.body`} />
        </span>
      </div>
      {action ? (
        <Link className="btn btn-primary btn-sm" to={action.to}>
          <FormattedMessage id={action.id} />
          <Icon name="arrowRight" size={15} />
        </Link>
      ) : step === "l2_available" ? (
        <span className="btn btn-outline btn-sm is-disabled" aria-disabled="true">
          <FormattedMessage id="dashboard.next.l2_available.cta" />
          <span className="soon-tag">
            <FormattedMessage id="common.comingSoon" />
          </span>
        </span>
      ) : null}
    </div>
  );
}

function LevelMeter({ level }) {
  return (
    <ol className="level-meter">
      {[0, 1, 2].map((l) => (
        <li key={l} className={l <= level ? "is-on" : ""}>
          <span className="level-badge">L{l}</span>
          <span className="small">
            <FormattedMessage id={`verification.level${l}`} />
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function Dashboard() {
  useDocumentTitle("dashboard.title");
  const lp = useLocalePath();
  const errMsg = useErrorMessage();
  const { tenant, user, membership } = useSession();
  const [invs, setInvs] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    endpoints.investigations().then((d) => setInvs(d.investigations || []), setError);
  };
  useEffect(load, []);

  const canInvestigate = tenant && tenant.verificationLevel >= 1 && tenant.status === "active";

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">{membership?.tenantName}</p>
          <h1 className="h1">
            <FormattedMessage
              id="dashboard.heading"
              values={{
                email: user?.email,
                accent: (chunks) => <span className="grad-text">{chunks}</span>,
              }}
            />
          </h1>
        </div>
        <Link className={`btn btn-primary ${canInvestigate ? "" : "is-disabled"}`} to={lp("/investigations/new")}>
          <Icon name="plus" size={16} />
          <FormattedMessage id="nav.newInvestigation" />
        </Link>
      </header>

      {tenant && tenant.status !== "active" ? (
        <div className="banner banner-error" role="alert">
          <Icon name="alert" />
          <span className="banner-text">
            <FormattedMessage id={`tenantStatus.${tenant.status}.notice`} />
          </span>
        </div>
      ) : null}

      <section className="stat-grid">
        <div className="card stat-card">
          <div className="stat-label">
            <Icon name="coins" size={16} />
            <FormattedMessage id="dashboard.credits" />
          </div>
          <div className="stat-value grad-text">
            <Credits value={tenant?.creditBalance ?? null} />
          </div>
          <div className="stat-foot muted small">
            {tenant?.plan ? (
              <FormattedMessage
                id="dashboard.plan"
                values={{ plan: <FormattedMessage id={`plan.${tenant.plan}`} defaultMessage={tenant.plan} /> }}
              />
            ) : (
              <FormattedMessage id="dashboard.noPlan" />
            )}
            <Link to={lp("/settings#credits")} className="link-sm">
              <FormattedMessage id="dashboard.viewLedger" />
            </Link>
          </div>
        </div>

        <div className="card stat-card stat-wide">
          <div className="stat-label">
            <Icon name="shield" size={16} />
            <FormattedMessage id="dashboard.verification" />
          </div>
          <LevelMeter level={tenant?.verificationLevel ?? 0} />
          <NextStep tenant={tenant} />
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="h3">
            <FormattedMessage id="dashboard.recent" />
          </h2>
          <Link to={lp("/investigations")} className="link-sm">
            <FormattedMessage id="common.viewAll" />
            <Icon name="chevronRight" size={14} />
          </Link>
        </div>
        <ErrorBanner message={error ? errMsg(error) : null} onRetry={load} />
        {!invs && !error ? <Loading /> : null}
        {invs && invs.length === 0 ? (
          <EmptyState
            icon="target"
            title={<FormattedMessage id="dashboard.emptyTitle" />}
            body={<FormattedMessage id="dashboard.emptyBody" />}
          />
        ) : null}
        {invs && invs.length > 0 ? (
          <ul className="recent-list">
            {invs.slice(0, RECENT).map((inv) => (
              <li key={inv.id}>
                <Link to={lp(`/investigations/${inv.id}`)} className="recent-item">
                  <span className={`type-badge type-${inv.targetType}`}>
                    <FormattedMessage id={`targetType.${inv.targetType}`} defaultMessage={inv.targetType} />
                  </span>
                  <span className="recent-main">
                    <strong className="truncate">{inv.name}</strong>
                    <span className="mono muted small truncate">{inv.target}</span>
                  </span>
                  <span className="muted small">
                    <FormattedDate value={inv.createdAt} dateStyle="medium" />
                  </span>
                  <StatusPill ns="invStatus" value={inv.status} />
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
